import { InvoiceFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { UtilsHelper } from "utils/helpers";
import useInvoicePreviewFormHelpers from "../useInvoicePreviewFormHelpers";
import useReminderValues from "../reminder/useReminderValues";
import { InvoiceConstants } from "utils/constants";
import useLastSavedMailOptions from "./useLastSendMailOptions";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";

function useGetMailOptionDefaults() {
  const { serialNumberTemplateValueDisplayText, getFormattedMessage } = useInvoicePreviewFormHelpers();
  const { getReminderValues } = useReminderValues();
  const { updateSendMailOptionsFromLastSaved } = useLastSavedMailOptions();

  const getEmailToDefault = (
    referencePersonEmail?: string,
    customerEmail?: string,
    sendMailToSettings?: InvoiceFormTypes.DefaultEmailToSettings
  ) => {
    let emailToValue: string | undefined = undefined;
    if (sendMailToSettings?.emailToType !== undefined) {
      emailToValue =
        sendMailToSettings?.emailToType === WintSuperkollAPIModel.SendInvoiceMailsToEnum.ReferencePerson
          ? referencePersonEmail
          : sendMailToSettings?.emailToType === WintSuperkollAPIModel.SendInvoiceMailsToEnum.CustomerCompany
          ? customerEmail
          : sendMailToSettings.customEmail;
    }
    return !UtilsHelper.isEmpty(emailToValue)
      ? emailToValue
      : !UtilsHelper.isEmpty(referencePersonEmail)
      ? referencePersonEmail
      : customerEmail;
  };

  const getCreateInvoiceDefaultEmailOptions = (
    sendMailOptions: InvoiceFormTypes.SendMailOptions,
    sendMailToSettings?: InvoiceFormTypes.DefaultEmailToSettings,
    lastSaved?: WintSuperkollAPIModel.InvoiceSendProperties
  ) => {
    const sendMailValues: InvoiceFormTypes.SendMail = {};

    sendMailValues.emailFromType = sendMailOptions.emailFromType;
    sendMailValues.emailFrom = sendMailOptions.emailFrom;

    const emailToValue = getEmailToDefault(
      sendMailOptions.defaultEmailTo?.referencePerson,
      sendMailOptions.defaultEmailTo?.customer,
      sendMailToSettings
    );
    sendMailValues.emailTo = UtilsHelper.splitMultipleEmails(emailToValue).map(
      (x: string): AutocompleteListItem => ({
        id: x,
        value: x,
      })
    );

    sendMailValues.emailMessage = sendMailOptions.mailMessage;

    sendMailValues.emailSubject = sendMailOptions.mailSubject.replace(
      InvoiceConstants.SerialNumberTemplateValue,
      serialNumberTemplateValueDisplayText
    );

    if (!lastSaved) return sendMailValues;

    return updateSendMailOptionsFromLastSaved(sendMailValues, lastSaved);
  };

  const getReminderDefaultEmailOptions = (
    formValues: {
      paymentReminderTitleTemplate?: string;
      paymentReminderEmailMessageTemplate?: string;
      serialNumber?: number;
    },
    lastSaved?: WintSuperkollAPIModel.InvoiceSendProperties
  ) => {
    const { paymentReminderEmailMessageTemplate, paymentReminderTitleTemplate, serialNumber } = formValues;
    const { reminderTitleTemplate, reminderEmailMessageTemplate } = getReminderValues(
      paymentReminderEmailMessageTemplate,
      paymentReminderTitleTemplate,
      undefined,
      serialNumber
    );

    const sendMailValues: InvoiceFormTypes.SendMail = updateSendMailOptionsFromLastSaved({}, lastSaved);

    sendMailValues.emailMessage = getFormattedMessage(
      reminderEmailMessageTemplate ?? "",
      InvoiceConstants.ReminderDueDays
    );

    sendMailValues.emailSubject = getFormattedMessage(reminderTitleTemplate, InvoiceConstants.ReminderDueDays);

    return sendMailValues;
  };

  return { getCreateInvoiceDefaultEmailOptions, getReminderDefaultEmailOptions, getEmailToDefault };
}

export default useGetMailOptionDefaults;
